import { get } from "lodash";
import { CONTACT_POINT_SYSTEM, REGEX_EXPS } from "utils/constants";
import { normalizeTextField, stripAllSpaces } from "utils/helpers/validations";
import { validateIBAN } from "utils/helpers/values";
import * as yup from "yup";
import {
  attachAcorisForeignCountryTest,
  isAcorisZipCode,
} from "../utils/zipCode";

const addressTelecomSchema = (messages, textField) =>
  attachAcorisForeignCountryTest(
    yup.object().shape({
      [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
        value: textField
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED)
          .nullable(),
      }),
      [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
        value: yup
          .string()
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED)
          .test("acoris-zip", messages.INVALID_VALUE, (value) =>
            isAcorisZipCode(value),
          )
          .nullable(),
      }),
      [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
        value: yup
          .string()
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED)
          .nullable(),
      }),
      [CONTACT_POINT_SYSTEM.COUNTRY]: yup.object().shape({
        value: textField.nullable(),
      }),
    }),
    messages.REQUIRED,
  );

const validation = (messages, { isPayerReceiver = true } = {}) => {
  const string = yup
    .string()
    .typeError(messages.NOT_STRING)
    .required(messages.REQUIRED)
    .nullable();
  const noSpaceString = yup.string().transform(stripAllSpaces);
  const nameValidation = yup
    .string()
    .transform(normalizeTextField)
    .typeError(messages.NOT_STRING)
    .nullable()
    .notRequired()
    .test(
      "is-valid-name",
      messages.INVALID_NAME,
      (value) => !value || REGEX_EXPS.name.test(value),
    );
  const requiredName = yup
    .string()
    .transform(normalizeTextField)
    .typeError(messages.NOT_STRING)
    .required(messages.REQUIRED)
    .test(
      "is-valid-name",
      messages.INVALID_NAME,
      (value) => !value || REGEX_EXPS.name.test(value),
    );
  const textField = yup.string().transform(normalizeTextField);

  return yup.object().shape({
    isPayerReceiver: yup.boolean().nullable(),
    payment: yup.object().shape({
      type: string,
      frequency: string,
      debitDay: string,
      payer: yup.object().shape({
        BIC: noSpaceString
          .typeError(messages.NOT_STRING)
          .required(messages.REQUIRED)
          .nullable(),
        bankName: textField
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED)
          .nullable(),
        bankAddress: textField
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED)
          .nullable(),
        IBAN: noSpaceString
          .typeError(messages.REQUIRED)
          .test("validateIBAN", messages.INVALID_IBAN, function (value, ctx) {
            const result = validateIBAN(value);
            if (!result.valid)
              return ctx.createError({
                message: get(messages, result.reason, messages.INVALID_VALUE),
              });

            return true;
          })
          .required(messages.REQUIRED),
        owner: yup.object().shape({
          isAdherent: yup.boolean().typeError(messages.NOT_BOOLEAN),
          firstName: yup.string().when("isAdherent", {
            is: false,
            then: () => requiredName,
            otherwise: () => nameValidation,
          }),
          lastName: yup.string().when("isAdherent", {
            is: false,
            then: () => requiredName,
            otherwise: () => nameValidation,
          }),
          gender: yup
            .string()
            .typeError(messages.NOT_STRING)
            .required(messages.REQUIRED)
            .nullable(),
        }),
      }),
      receiver: isPayerReceiver
        ? yup.object().nullable().notRequired()
        : yup.object().shape({
            IBAN: noSpaceString
              .typeError(messages.REQUIRED)
              .test(
                "validateIBAN",
                messages.INVALID_IBAN,
                function (ibanValue) {
                  if (!ibanValue) return false;
                  const result = validateIBAN(ibanValue);
                  if (!result.valid)
                    return this.createError({
                      message: get(
                        messages,
                        result.reason,
                        messages.INVALID_VALUE,
                      ),
                    });
                  return true;
                },
              )
              .test(
                "different-from-payer",
                get(
                  messages,
                  "RECEIVER_IBAN_SAME_AS_PAYER",
                  messages.RECEIVER_IBAN_SAME_AS_PAYER,
                ),
                function (ibanValue) {
                  if (!ibanValue) return true;
                  const paymentValues = this.from?.[1]?.value;
                  const payerIBAN = paymentValues?.payer?.IBAN;
                  if (!payerIBAN) return true;
                  return ibanValue !== payerIBAN;
                },
              )
              .required(messages.REQUIRED),
            BIC: noSpaceString
              .typeError(messages.NOT_STRING)
              .required(messages.REQUIRED)
              .nullable(),
            owner: yup.object().shape({
              firstName: requiredName,
              lastName: requiredName,
              gender: yup
                .string()
                .typeError(messages.NOT_STRING)
                .required(messages.REQUIRED)
                .nullable(),
              isAdherent: yup.boolean().typeError(messages.NOT_BOOLEAN),
            }),
          }),
    }),
    telecoms: yup
      .object()
      .shape({
        payer: yup.object().nullable().notRequired(),
        receiver: yup.object().nullable().notRequired(),
      })
      .test(
        "third-party-payer-address",
        messages.REQUIRED,
        function (telecoms) {
          const isAdherent = get(
            this.parent,
            "payment.payer.owner.isAdherent",
            true,
          );
          if (isAdherent) return true;
          try {
            addressTelecomSchema(messages, textField).validateSync(
              get(telecoms, "payer"),
              { abortEarly: false },
            );
            return true;
          } catch (error) {
            const path = get(
              error,
              "inner.0.path",
              `${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
            );
            return this.createError({
              path: `telecoms.payer.${path}`,
              message: get(error, "inner.0.message", messages.REQUIRED),
            });
          }
        },
      ),
  });
};

export default validation;
